import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserIfNeeded, selectUserById } from '../redux/slices/usersCacheSlice';
import { batchedUserFetcher } from '../utils/performanceOptimizations';

/**
 * useUserProfile
 * Lightweight selector + fetch-on-demand for other users' profiles.
 * Cache-first with TTL, powered by usersCacheSlice.
 *
 * @param {string} userId
 * @param {{force?: boolean}} options
 * @returns {{ profile: object|null, loading: boolean, error: string|null }}
 */
export default function useUserProfile(userId, options = {}) {
  const dispatch = useDispatch();
  const profile = useSelector((s) => (userId ? selectUserById(s, userId) : null));
  const status = useSelector((s) => (userId ? s.usersCache?.status?.[userId] : 'idle'));
  const error = useSelector((s) => (userId ? s.usersCache?.error?.[userId] : null));
  const loading = status === 'loading';
  const force = !!options.force;
  const enableBatch = process.env.REACT_APP_ENABLE_BATCHED_USER_FETCH === 'true';

  useEffect(() => {
    if (!userId) return;
    // Forced refresh always uses single-user thunk to bypass TTL
    if (force) {
      dispatch(fetchUserIfNeeded({ userId, force: true }));
      return;
    }
    // If missing profile, fetch (optionally via batcher) with rate limiting
    if (!profile) {
      if (enableBatch && batchedUserFetcher && batchedUserFetcher.dispatch) {
        batchedUserFetcher.fetchUser(userId);
      } else {
        // Add delay to prevent excessive individual fetches during user switching
        dispatch(fetchUserIfNeeded({ userId, force: false }));
      }
    }
  }, [dispatch, userId, force, profile, enableBatch]);

  return useMemo(() => ({ profile, loading, error }), [profile, loading, error]);
}
